import { useMemo } from 'react';
import { PaymentType } from '@frontend/api-invoices';
import { PaymentMethod, PaymentMethodStatus } from '@frontend/api-payment-plans';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { PaymentMethodCardUI, PaymentMethodCardUIAction } from '@frontend/payments-card-on-file';
import { classNames, titleCase } from '@frontend/string';

export type PaymentMethodAchProps = {
  isSelected: boolean;
  minAchAmount: number;
  achDisabledMessage?: string;
  paymentMethod: PaymentMethod;
  disableAchPaymentMethods: boolean;
  className?: string;
  onSelectPaymentMethod: (paymentMethod: PaymentMethod) => void;
  onClickResendVerification: () => void;
  onClickDelete: (pm: PaymentMethod) => void;
};
export const PaymentMethodAch = ({
  className,
  isSelected,
  minAchAmount,
  paymentMethod,
  disableAchPaymentMethods,
  achDisabledMessage,
  onSelectPaymentMethod,
  onClickResendVerification,
  onClickDelete,
}: PaymentMethodAchProps) => {
  const { t } = useTranslation('payments');

  const isVerificationPending = paymentMethod.status === PaymentMethodStatus.PAYMENT_METHOD_STATUS_VERIFICATION_PENDING;
  const title = isVerificationPending
    ? t('Customer Verification Pending')
    : titleCase(paymentMethod.bankAccount.bankName);
  const description = isVerificationPending ? t('Have customer validate through the link in their email') : undefined;
  const last4 = isVerificationPending ? '' : paymentMethod.bankAccount?.last4;

  const isDisabled =
    disableAchPaymentMethods ||
    (paymentMethod.status &&
      [
        PaymentMethodStatus.PAYMENT_METHOD_STATUS_VERIFICATION_PENDING,
        PaymentMethodStatus.PAYMENT_METHOD_STATUS_EXPIRED,
      ].includes(paymentMethod.status));

  const actions = useMemo(() => {
    const actionList: PaymentMethodCardUIAction[] = [];
    if (isVerificationPending) {
      actionList.push({
        icon: 'send-back',
        label: t('Resend Verification'),
        onClick: onClickResendVerification,
      });
    } else {
      actionList.push({
        icon: 'trash',
        label: t('Delete'),
        onClick: () => onClickDelete(paymentMethod),
        disabled: disableAchPaymentMethods,
      });
    }

    return actionList;
  }, [isVerificationPending, disableAchPaymentMethods, paymentMethod, onClickDelete, onClickResendVerification, t]);

  const disableAchPaymentMessage =
    achDisabledMessage ??
    t('ACH only available for payments over {{minimumAmount}}.', {
      minimumAmount: formatCentsToCurrency(minAchAmount),
    });

  return (
    <PaymentMethodCardUI
      className={classNames('payment-methods__payment-method--ach', className)}
      brand={PaymentType.ACH}
      title={title}
      description={description}
      last4={last4}
      methodId={paymentMethod.id}
      selected={isSelected}
      disabled={isDisabled}
      status={paymentMethod.status}
      onClick={() => onSelectPaymentMethod(paymentMethod)}
      tooltipMessage={disableAchPaymentMethods ? disableAchPaymentMessage : ''}
      actions={actions}
    />
  );
};
